import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { ApiError, api } from '../api/client';
import { GoogleIcon, MicrosoftIcon, PersonIcon } from '../components/AuthIcons';
import { AuthField, AuthFieldSlot, AuthPasswordField } from '../components/AuthField';
import { OtpInput } from '../components/OtpInput';
import { OtpResendPrompt } from '../components/OtpResendPrompt';
import { stagePromoCreditWelcome } from '../components/PromoCreditWelcomeModal';
import { useAuthCardMotion, authFieldVariants } from '../components/AuthSplitShell';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { isOtpComplete } from '../lib/otp';
import { readNextPath, withNextParam } from '../lib/nextPath';

function MotionField({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div className={className} variants={authFieldVariants}>
      {children}
    </motion.div>
  );
}


/**
 * Sign-in, including the second factor.
 *
 * Two things here are contract requirements rather than UI preferences:
 *
 *  - The server decides which second factors this account may use, from the
 *    account type and the *verified phone country* — never from the client and
 *    never from an IP. This screen renders what it is told.
 *  - The label is "Authenticator app" (RFC 6238), never "Google Authenticator".
 *    Any TOTP app works, and naming one vendor sends people to the wrong place.
 */
export function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const { dict } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Google sign-in is a full-page redirect, not a fetch — there is no XHR
  // response for a handler here to read. member-api's
  // /auth/google/callback (oauth_google.go) instead lands the browser back
  // on THIS page with `?challenge_id=…` (an enrolled factor exists;
  // continue into the SAME 2fa/verify step the password path already has)
  // or `?error=…`.
  //
  // Read synchronously into the initial state — NOT in a useEffect that
  // calls setChallengeId after mount. That was tried first and left the
  // split-panel form stuck at opacity 0 forever, reproducible even in a
  // production build (so not a StrictMode artifact): mounting into
  // "credentials" and then, a tick later, swapping the AnimatePresence
  // `key` to "code" interrupts the credentials form's own enter transition
  // before Framer Motion has scheduled its first frame, and `mode="wait"`
  // never recovers from that. Initialising state from the URL up front
  // means the very first render already has the right key — no swap, no
  // interrupted transition, and no flash of the credentials form first.
  const [challengeId, setChallengeId] = useState<string | null>(
    () => new URLSearchParams(location.search).get('challenge_id'),
  );
  const [methods, setMethods] = useState<string[]>([]);
  // null until a password login populates `methods` and picks one — see
  // activeMethod below. Stays null for the Google-redirect entry (the
  // callback lands here with only ?challenge_id=, no methods list, so this
  // falls back to the same totp-only behaviour the flow always had).
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const activeMethod = selectedMethod ?? 'totp';
  // Only sms/email_otp ever populate this — totp needs no send step, so
  // there is nothing to report a destination for.
  const [destinationMasked, setDestinationMasked] = useState<string | null>(null);
  const [sendBusy, setSendBusy] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  type ErrorState =
    | { kind: 'oauth'; code: string }
    | { kind: 'tooMany' }
    | { kind: 'emailNotVerified' }
    | { kind: 'twoFactorSetupRequired' }
    | { kind: 'credentials' }
    | { kind: 'sendFailed' }
    | { kind: 'recoveryInvalid' }
    | { kind: 'codeInvalid' };

  const [errorState, setErrorState] = useState<ErrorState | null>(() => {
    const err = new URLSearchParams(location.search).get('error');
    return err ? { kind: 'oauth', code: err } : null;
  });
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Controls both whether member-api issues a persistent session cookie
  // (Max-Age set) vs. a browser-session one (cleared on browser close), and
  // how long the session itself is valid server-side — 12h unchecked, 24h
  // checked (see member-api's sessionTTL / rememberedSessionTTL). Defaults
  // to true for the Google-redirect entry (a `challenge_id` already in the
  // URL at mount): that flow never shows this checkbox, so it must not
  // silently downgrade the "always persistent" behaviour Google sign-in had
  // before this checkbox did anything.
  const [rememberMe, setRememberMe] = useState(
    () => new URLSearchParams(location.search).get('challenge_id') != null,
  );

  // Falls through to App.tsx's own default (the signed-in route tree's
  // "/" → /dashboard redirect) when there is no valid `next` — only
  // overrides it for the "arrived via a direct link to a signed-in-only
  // page" case (see App.tsx's RedirectToLogin).
  function finishSignIn() {
    onSignedIn();
    const next = readNextPath(location.search);
    if (next) navigate(next, { replace: true });
  }

  const cardMotion = useAuthCardMotion(challengeId ? 'signin-code' : 'signin-credentials');

  const error =
    errorState == null
      ? null
      : errorState.kind === 'oauth'
        ? dict.signIn.oauthError(errorState.code)
        : errorState.kind === 'tooMany'
          ? dict.signIn.errTooMany
          : errorState.kind === 'emailNotVerified'
            ? dict.signIn.errEmailNotVerified
            : errorState.kind === 'twoFactorSetupRequired'
              ? dict.signIn.errTwoFactorSetupRequired
              : errorState.kind === 'credentials'
              ? dict.signIn.errCredentials
              : errorState.kind === 'sendFailed'
                ? dict.signIn.errSendFailed
                : errorState.kind === 'recoveryInvalid'
                  ? dict.signIn.errRecoveryInvalid
                  : dict.signIn.errCodeInvalid;

  // Strips `challenge_id`/`error` from the URL once the state above has
  // already read them, so a refresh cannot replay a used challenge_id. Pure
  // cleanup — no state changes here, which is what makes it harmless if
  // StrictMode's dev-only double-invoke fires it twice.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has('challenge_id') && !params.has('error')) return;
    params.delete('challenge_id');
    params.delete('error');
    const rest = params.toString();
    navigate({ pathname: location.pathname, search: rest ? `?${rest}` : '' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sends (or resends) a code for an sms/email_otp method. Never called for
  // totp — that code already exists in the customer's authenticator app.
  async function sendOTP(id: string, method: string, isResend: boolean) {
    setSendBusy(true);
    setErrorState(null);
    setResendMessage(null);
    try {
      const res = await api.sendTwoFactorOTP(id, method as 'sms' | 'email_otp');
      setDestinationMasked(res.destination_masked);
      if (isResend) setResendMessage(dict.signIn.resendSent);
    } catch {
      setErrorState({ kind: 'sendFailed' });
    } finally {
      setSendBusy(false);
    }
  }

  // Switches which confirmed method the code screen is acting on — called
  // once automatically after a password login (picking totp when available,
  // since PreferredMethod on the server always prefers it too), and again
  // whenever the customer picks a different one from methods.length > 1.
  function selectMethod(id: string, method: string) {
    setSelectedMethod(method);
    setCode('');
    setErrorState(null);
    setDestinationMasked(null);
    setResendMessage(null);
    if (method !== 'totp') void sendOTP(id, method, false);
  }

  async function submitCredentials(e: React.FormEvent) {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    if (trimmedEmail !== email) setEmail(trimmedEmail);
    if (trimmedPassword !== password) setPassword(trimmedPassword);
    setBusy(true);
    setErrorState(null);
    try {
      const result = await api.login(trimmedEmail, trimmedPassword, rememberMe);
      if (result.status === 'two_factor_required') {
        setChallengeId(result.challenge_id);
        const m = result.methods ?? [];
        setMethods(m);
        selectMethod(result.challenge_id, m.includes('totp') ? 'totp' : (m[0] ?? 'totp'));
      } else {
        if (result.promo_credit) stagePromoCreditWelcome(result.promo_credit);
        finishSignIn();
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setErrorState({ kind: 'tooMany' });
      } else if (err instanceof ApiError && err.is('/problems/email-not-verified')) {
        // Safe to be specific here: this only fires after a CORRECT password
        // (see the ordering note on member-api's handleLogin), so it cannot
        // be used to test whether an email has an account at all.
        setErrorState({ kind: 'emailNotVerified' });
      } else if (err instanceof ApiError && err.is('/problems/two-factor-required')) {
        // Same ordering guarantee as email-not-verified above — handleLogin
        // only reaches this check after a correct password. Previously fell
        // through to the generic 'credentials' bucket, which read as "wrong
        // password" for an account that in fact had the right one but could
        // never sign in at all (mandatory 2FA, nothing enrolled yet) — very
        // different problems needing very different next steps.
        setErrorState({ kind: 'twoFactorSetupRequired' });
      } else {
        // Deliberately one message for both "no such account" and "wrong
        // password". Distinguishing them turns this form into an account
        // enumeration oracle.
        setErrorState({ kind: 'credentials' });
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    if (!challengeId) return;
    setBusy(true);
    setErrorState(null);
    try {
      const result = await api.verifyTwoFactor(
        challengeId,
        code.trim(),
        useRecoveryCode,
        activeMethod as 'totp' | 'sms' | 'email_otp',
        rememberMe,
      );
      if (result.promo_credit) stagePromoCreditWelcome(result.promo_credit);
      finishSignIn();
    } catch (err) {
      setErrorState(
        err instanceof ApiError && err.status === 429
          ? { kind: 'tooMany' }
          : useRecoveryCode
            ? { kind: 'recoveryInvalid' }
            : { kind: 'codeInvalid' },
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AnimatePresence mode="wait">
      {challengeId ? (
        <motion.form
          key="code"
          className="auth-split-form-inner"
          onSubmit={submitCode}
          {...cardMotion}
        >
                <motion.h1 variants={authFieldVariants}>{dict.signIn.twoStepTitle}</motion.h1>
                <motion.p className="auth-lede" variants={authFieldVariants}>
                  {useRecoveryCode
                    ? dict.signIn.ledeRecovery
                    : activeMethod === 'totp'
                      ? dict.signIn.ledeAuthenticator
                      : sendBusy && !destinationMasked
                        ? dict.signIn.sendingCode
                        : destinationMasked
                          ? dict.signIn.ledeOTPSent(destinationMasked)
                          : dict.signIn.sendingCode}
                </motion.p>
                {error && (
                  <MotionField>
                    <div className="err">{error}</div>
                  </MotionField>
                )}

                {/* Only shown when more than one confirmed method exists —
                    a single-method account has nothing to switch between,
                    matching how the old plain-text note never appeared for
                    fewer than... it just listed what existed either way, but
                    an actual switcher control only earns its place here. */}
                {!useRecoveryCode && methods.length > 1 && (
                  <MotionField>
                    <div className="method-switch" role="group" aria-label={dict.signIn.chooseMethodLede}>
                      {methods.map((m) => (
                        <button
                          key={m}
                          type="button"
                          className={m === activeMethod ? '' : 'ghost'}
                          disabled={busy || sendBusy}
                          onClick={() => challengeId && selectMethod(challengeId, m)}
                        >
                          {dict.signIn.methodName(m)}
                        </button>
                      ))}
                    </div>
                  </MotionField>
                )}

                {useRecoveryCode ? (
                  <MotionField>
                    <AuthField
                      id="code"
                      label={dict.signIn.recoveryCodeLabel}
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      autoComplete="one-time-code"
                      inputMode="text"
                      autoFocus
                      invalid={errorState?.kind === 'recoveryInvalid'}
                    />
                  </MotionField>
                ) : (
                  <MotionField>
                    <AuthFieldSlot id="signin-otp-label" label={
                      activeMethod === 'totp'
                        ? dict.signIn.authCodeLabel
                        : dict.signIn.otpCodeLabel
                    } className="auth-field--plain">
                      <OtpInput
                        id="signin-otp"
                        value={code}
                        onChange={setCode}
                        autoFocus
                        aria-invalid={errorState?.kind === 'codeInvalid'}
                        aria-labelledby="signin-otp-label"
                        digitAriaLabel={dict.common.otpDigitLabel}
                      />
                    </AuthFieldSlot>
                  </MotionField>
                )}

                {!useRecoveryCode && activeMethod !== 'totp' && (
                  <MotionField>
                    <OtpResendPrompt
                      prompt={dict.common.otpDidntGetCode}
                      actionLabel={dict.common.otpResendLink}
                      busyLabel={dict.signIn.sendingCode}
                      busy={sendBusy}
                      disabled={!challengeId}
                      sentMessage={resendMessage}
                      onResend={() => challengeId && void sendOTP(challengeId, activeMethod, true)}
                    />
                  </MotionField>
                )}

                <MotionField>
                  <button
                    type="submit"
                    disabled={busy || (useRecoveryCode ? code.trim() === '' : !isOtpComplete(code))}
                    className="btn-block"
                  >
                    {busy ? dict.signIn.checking : dict.signIn.continue}
                  </button>
                </MotionField>

                <MotionField>
                  <button
                    type="button"
                    className="ghost btn-block"
                    onClick={() => {
                      setUseRecoveryCode(!useRecoveryCode);
                      setCode('');
                      setErrorState(null);
                    }}
                  >
                    {useRecoveryCode ? dict.signIn.useAuthenticatorInstead : dict.signIn.useRecoveryInstead}
                  </button>
                </MotionField>

                <motion.p className="note" variants={authFieldVariants}>{dict.signIn.recoveryHelp}</motion.p>
              </motion.form>
            ) : (
              <motion.form
                key="credentials"
                className="auth-split-form-inner"
                onSubmit={submitCredentials}
                {...cardMotion}
              >
                <motion.h1 variants={authFieldVariants}>{dict.signIn.signInTitle}</motion.h1>
                <motion.p className="auth-lede" variants={authFieldVariants}>{dict.signIn.ledeSignIn}</motion.p>
                {error && (
                  <MotionField>
                    <div className="err">{error}</div>
                  </MotionField>
                )}

                <MotionField>
                  <AuthField
                    id="email"
                    label={dict.signIn.emailLabel}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    autoFocus
                    trailing={<PersonIcon />}
                  />
                </MotionField>

                <MotionField>
                  <AuthPasswordField
                    id="password"
                    label={dict.signIn.passwordLabel}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    showPassword={showPassword}
                    onToggleShow={() => setShowPassword(!showPassword)}
                    showLabel={dict.signIn.showPassword}
                    hideLabel={dict.signIn.hidePassword}
                  />
                </MotionField>

                <MotionField className="remember-row">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    {dict.signIn.rememberMe}
                  </label>
                  <Link to="/forgot-password">
                    {dict.signIn.forgotPassword}
                  </Link>
                </MotionField>

                <MotionField>
                  <button type="submit" disabled={busy || !email.trim() || !password.trim()} className="btn-block">
                    {busy ? dict.signIn.signingIn : dict.signIn.signIn}
                  </button>
                </MotionField>

                <MotionField>
                  <div className="auth-or-divider" role="separator" aria-label={dict.signIn.orDivider}>
                    <span>{dict.signIn.orDivider}</span>
                  </div>
                </MotionField>

                {/* Both are plain links (full-page redirect to consent) —
                    same reasoning as Google's, see oauth_google.go's
                    handleGoogleStart doc comment on why a link, not a fetch. */}
                <MotionField>
                  <div className="sso-icon-row">
                    <a
                      href="/api/auth/google/start"
                      className="ghost btn-sso-icon"
                      aria-label={dict.signIn.signInWithGoogle}
                    >
                      <GoogleIcon />
                    </a>
                    <a
                      href="/api/auth/microsoft/start"
                      className="ghost btn-sso-icon"
                      aria-label={dict.signIn.signInWithMicrosoft}
                    >
                      <MicrosoftIcon />
                    </a>
                  </div>
                </MotionField>

                <motion.p className="note" variants={authFieldVariants}>
                  {dict.signIn.noAccount}{' '}
                  <Link to={withNextParam('/signup', location.search)}>{dict.signIn.signUpLink}</Link>
                </motion.p>
              </motion.form>
            )}
    </AnimatePresence>
  );
}
